/**
 * GCS Resumable Upload (Browser side) -- issue #362
 *
 * Two-step protocol per the V4 resumable signed URL flow:
 *
 *   1. POST the signed URL with header `x-goog-resumable: start` and an
 *      empty body. GCS responds 201 with `Location: <sessionUri>` in the
 *      response headers. The session URI is independently valid for 7
 *      days and is its own auth token -- no further signing required.
 *
 *   2. PUT chunks to the session URI with `Content-Range`. We chunk at
 *      8MB so a flaky mobile connection only loses one chunk at a time,
 *      and so the user sees progress updates more often than every 4GB.
 *
 * Retries: each chunk is attempted up to 3 times with exponential
 * backoff. If a PUT returns 308 (Resume Incomplete) mid-stream that's
 * normal -- it just means more bytes are expected.
 *
 * Cancellation: pass an AbortSignal. We check it before each chunk and
 * abort the in-flight fetch. The session URI itself is left dangling --
 * the bucket's 24h lifecycle policy cleans it up.
 */

const CHUNK_SIZE = 8 * 1024 * 1024 // 8 MiB
const MAX_CHUNK_ATTEMPTS = 3
const CHUNK_RETRY_BASE_DELAY_MS = 1000
/**
 * Skip the client-side duration probe for files above this size. Camera-
 * original MOVs put the moov atom at end-of-file, so `<video>` with
 * preload="metadata" reads ~all the bytes locating it -- 10-30s of UI
 * stall and a memory spike on a 2 GB file. The server re-checks duration
 * via ffprobe (RITUAL_VIDEO_MAX_DURATION_SECONDS) so we don't lose the
 * cap, we just shift it from client-side fast-fail to post-upload reject.
 */
const PROBE_SKIP_SIZE_BYTES = 500 * 1024 * 1024
// Hard ceiling on the hidden-<video> metadata probe. For some files/codecs the
// browser fires neither `loadedmetadata` nor `error`, leaving the probe Promise
// unsettled forever and hanging any caller that awaits it. If the probe hasn't
// settled by this point, give up and defer the duration check to the server.
const PROBE_TIMEOUT_MS = 10_000
const PROGRESS_TICK_MS = 250

/**
 * Per-request stall guards. The browser Fetch API exposes no upload progress,
 * so we can't reset a timer on bytes-sent; these are generous ceilings that
 * surface a DEAD connection -- which would otherwise hang the card at "0%"
 * forever with no error and no retry -- without tripping a merely slow one. A
 * timed-out request is treated as a retryable network error, distinct from a
 * user abort (which is terminal).
 */
const SESSION_INIT_TIMEOUT_MS = 60_000
const CHUNK_TIMEOUT_MS = 120_000

export interface UploadResumableInput {
  signedUrl: string
  file: File
  contentType: string
  signal?: AbortSignal
  onProgress?: (uploadedBytes: number, totalBytes: number) => void
}

class AbortedError extends Error {
  constructor() {
    super("Upload aborted")
    this.name = "AbortError"
  }
}

class TimeoutError extends Error {
  constructor() {
    super("Upload request timed out")
    this.name = "TimeoutError"
  }
}

/**
 * True for an intentional cancellation -- our AbortedError or a native
 * DOMException from an aborted fetch (whose message is the unhelpful "signal is
 * aborted without reason"). Callers use this to swallow aborts instead of
 * surfacing them as upload-failed errors. Checks `name` rather than
 * `instanceof Error` because DOMException isn't always an Error subclass.
 */
export const isAbortError = (error: unknown): boolean =>
  typeof error === "object" && error !== null && (error as { name?: string }).name === "AbortError"

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw new AbortedError()
  }
}

/**
 * fetch() with a stall timeout, combined with the caller's abort signal.
 * Throws TimeoutError (retryable) when the timer fires, AbortedError (terminal)
 * when the caller aborts. Both reasons trigger the same underlying fetch abort;
 * we disambiguate via the `timedOut` flag so the retry loop treats them
 * differently.
 */
const fetchWithTimeout = async (
  input: string,
  init: RequestInit,
  timeoutMs: number,
  externalSignal?: AbortSignal,
): Promise<Response> => {
  throwIfAborted(externalSignal)
  const controller = new AbortController()
  const onExternalAbort = () => controller.abort()
  externalSignal?.addEventListener("abort", onExternalAbort, { once: true })
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)
  try {
    const res = await fetch(input, { ...init, signal: controller.signal })
    // fetch can resolve in the same tick the caller aborted; honor the abort/
    // timeout rather than returning a Response for a cancelled request.
    if (externalSignal?.aborted) throw new AbortedError()
    if (timedOut) throw new TimeoutError()
    return res
  } catch (error) {
    // A user abort is always terminal; only a pure timeout is retryable, so
    // check the external signal first when both happen at once.
    if (externalSignal?.aborted) throw new AbortedError()
    if (timedOut) throw new TimeoutError()
    throw error
  } finally {
    clearTimeout(timer)
    externalSignal?.removeEventListener("abort", onExternalAbort)
  }
}

const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer)
      reject(new AbortedError())
    }
    const timer = setTimeout(() => {
      // Remove the listener on the normal path too -- otherwise across a
      // multi-GB upload's retries these accumulate on the long-lived signal.
      signal?.removeEventListener("abort", onAbort)
      resolve()
    }, ms)
    signal?.addEventListener("abort", onAbort, { once: true })
  })

/**
 * Step 1: initiate the resumable upload by POSTing the signed URL.
 * Returns the session URI from the Location header.
 *
 * The backend signs `x-goog-content-length-range: 0,<totalBytes>` into
 * the URL, so we MUST echo that header on the POST -- GCS rejects with
 * MalformedSecurityHeader if the request omits any header that's in the
 * signature's signed-headers list. We don't set Content-Length itself
 * (forbidden header in browser Fetch -- the browser sets it from the
 * empty body).
 */
const initiateSession = async (
  signedUrl: string,
  contentType: string,
  totalBytes: number,
  signal?: AbortSignal,
): Promise<string> => {
  throwIfAborted(signal)
  const res = await fetchWithTimeout(
    signedUrl,
    {
      method: "POST",
      headers: {
        "x-goog-resumable": "start",
        "Content-Type": contentType,
        "x-goog-content-length-range": `0,${totalBytes}`,
      },
    },
    SESSION_INIT_TIMEOUT_MS,
    signal,
  )
  if (!res.ok && res.status !== 201) {
    throw new Error(`Failed to initiate resumable upload: ${res.status} ${await res.text()}`)
  }
  const sessionUri = res.headers.get("Location") ?? res.headers.get("location")
  if (!sessionUri) {
    throw new Error("GCS did not return a session URI (missing Location header)")
  }
  return sessionUri
}

/**
 * PUT a single chunk to the session URI, with retries on transient
 * failures (5xx, network errors). Returns the byte offset reported by
 * GCS in the Range header (0-indexed inclusive upper bound) once the
 * chunk lands. For the final chunk, GCS returns 200/201 with no Range
 * header; we treat that as "fully uploaded".
 */
const uploadChunk = async (params: {
  sessionUri: string
  chunk: Blob
  startByte: number
  endByteInclusive: number
  totalBytes: number
  signal?: AbortSignal
}): Promise<{ done: boolean; bytesUploaded: number }> => {
  const { sessionUri, chunk, startByte, endByteInclusive, totalBytes, signal } = params
  const contentRange = `bytes ${startByte}-${endByteInclusive}/${totalBytes}`

  let lastError: unknown
  for (let attempt = 0; attempt < MAX_CHUNK_ATTEMPTS; attempt++) {
    throwIfAborted(signal)
    // Catch network/abort errors only -- response-code handling is below
    // so we can `return` (success) or `throw` (terminal 4xx) without the
    // catch swallowing it back into the retry loop.
    let res: Response
    try {
      res = await fetchWithTimeout(
        sessionUri,
        {
          method: "PUT",
          headers: { "Content-Range": contentRange },
          body: chunk,
        },
        CHUNK_TIMEOUT_MS,
        signal,
      )
    } catch (error) {
      // A user abort (AbortError) is terminal; a stall TimeoutError or any other
      // network error falls through to the retry budget below.
      if ((error as Error).name === "AbortError") throw error
      lastError = error
      if (attempt < MAX_CHUNK_ATTEMPTS - 1) {
        await delay(CHUNK_RETRY_BASE_DELAY_MS * 2 ** attempt, signal)
      }
      continue
    }

    if (res.status === 200 || res.status === 201) {
      return { done: true, bytesUploaded: totalBytes }
    }

    if (res.status === 308) {
      // Resume Incomplete -- partial chunk landed, more expected.
      // Range header is like "bytes=0-12345"; the upper bound + 1 is
      // the next byte offset to send.
      const range = res.headers.get("Range") ?? res.headers.get("range")
      const match = range?.match(/bytes=\d+-(\d+)/)
      if (match) {
        return { done: false, bytesUploaded: Number.parseInt(match[1], 10) + 1 }
      }
      // No Range header on 308 -- GCS accepted nothing usable. Treat as
      // transient so the retry budget kicks in (returning startByte
      // would replay the same chunk forever).
      lastError = new Error("GCS 308 without Range header (no progress)")
    } else if (res.status >= 500 && res.status < 600) {
      lastError = new Error(`GCS ${res.status}: ${await res.text()}`)
    } else {
      // 4xx is terminal for this chunk -- signature mismatch, expired
      // session URI, etc. No point retrying.
      throw new Error(`GCS ${res.status}: ${await res.text()}`)
    }

    if (attempt < MAX_CHUNK_ATTEMPTS - 1) {
      await delay(CHUNK_RETRY_BASE_DELAY_MS * 2 ** attempt, signal)
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError))
}

export const uploadResumable = async (input: UploadResumableInput): Promise<void> => {
  const totalBytes = input.file.size
  const sessionUri = await initiateSession(
    input.signedUrl,
    input.contentType,
    totalBytes,
    input.signal,
  )

  let offset = 0
  input.onProgress?.(0, totalBytes)

  while (offset < totalBytes) {
    throwIfAborted(input.signal)
    const chunkEnd = Math.min(offset + CHUNK_SIZE, totalBytes)
    const chunk = input.file.slice(offset, chunkEnd)
    const endByteInclusive = chunkEnd - 1
    const chunkBytes = chunkEnd - offset

    // Synthesize interpolated progress while the chunk PUT is in flight.
    // Browser fetch doesn't expose upload progress, so without this the
    // bar jumps in 8 MB steps -- ~12 ticks on a 100 MB upload, which
    // looks stalled between ticks. We tween linearly toward the end of
    // the chunk, capped at 95% of the chunk so the user doesn't see a
    // dip if GCS reports a partial accept (308). The real value lands
    // when the chunk completes.
    const tickStart = offset
    const tickCap = offset + Math.floor(chunkBytes * 0.95)
    const chunkStartMs = Date.now()
    const estimatedChunkMs = Math.max(1000, chunkBytes / (4 * 1024)) // ~4 MB/s floor
    const tickHandle = setInterval(() => {
      const elapsed = Date.now() - chunkStartMs
      const fraction = Math.min(0.95, elapsed / estimatedChunkMs)
      const synthetic = tickStart + Math.floor(chunkBytes * fraction)
      input.onProgress?.(Math.min(synthetic, tickCap), totalBytes)
    }, PROGRESS_TICK_MS)

    let result: { done: boolean; bytesUploaded: number }
    try {
      result = await uploadChunk({
        sessionUri,
        chunk,
        startByte: offset,
        endByteInclusive,
        totalBytes,
        signal: input.signal,
      })
    } finally {
      clearInterval(tickHandle)
    }

    offset = result.bytesUploaded
    input.onProgress?.(offset, totalBytes)

    if (result.done) break
  }
}

/**
 * Probe a local video File for its duration via a hidden <video> element.
 * Used client-side to enforce the duration cap before requesting an
 * upload URL. Server re-checks via ffprobe in the worker, but failing
 * fast on the client gives the user a clearer error.
 *
 * Skipped for files above PROBE_SKIP_SIZE_BYTES (500 MB). Camera-original
 * MOVs put the moov atom at end-of-file, so the browser reads ~all the
 * bytes locating metadata -- on a 2 GB file that's a 10-30s UI stall
 * and a memory spike. Returns -1 to signal "unknown, defer to server"
 * so the caller can skip the cap check. The worker still enforces
 * RITUAL_VIDEO_MAX_DURATION_SECONDS post-upload via ffprobe.
 *
 * Also returns -1 (defer to server) if the probe does not settle within
 * PROBE_TIMEOUT_MS, since some files/codecs never fire loadedmetadata or error.
 */
export const probeVideoDuration = (file: File): Promise<number> => {
  if (file.size > PROBE_SKIP_SIZE_BYTES) {
    return Promise.resolve(-1)
  }
  return new Promise((resolve, reject) => {
    const video = document.createElement("video")
    video.preload = "metadata"
    const objectUrl = URL.createObjectURL(file)

    let settled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const cleanup = () => {
      if (timer) clearTimeout(timer)
      URL.revokeObjectURL(objectUrl)
      video.removeAttribute("src")
      video.load()
    }

    // Guard against the browser never firing loadedmetadata OR error: after the
    // timeout, resolve -1 so the caller defers the duration cap to the server
    // (same contract as the large-file skip above) instead of hanging forever.
    timer = setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
      resolve(-1)
    }, PROBE_TIMEOUT_MS)

    video.onloadedmetadata = () => {
      if (settled) return
      settled = true
      const duration = video.duration
      cleanup()
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error("Could not read video duration"))
        return
      }
      resolve(duration)
    }

    video.onerror = () => {
      if (settled) return
      settled = true
      cleanup()
      reject(new Error("Failed to read video metadata (corrupt or unsupported format)"))
    }

    video.src = objectUrl
  })
}
